import {
  UseMutationResult,
  useMutation,
  useQueryClient
} from '@tanstack/react-query'
import { graphql } from 'ms-client/graphql/user-pool'
import {
  Member_Role,
  UpgradeToMemberMutation,
  UpgradeToMemberMutationVariables
} from '~workspace/types/__generated/gql/graphql'
import {
  GetWorkspaceByIdResults,
  getWorkspaceByIdQueryKey
} from './useGetWorkspaceById'

const query = /* GraphQL */ `
  mutation UpgradeToMember($userId: ID!, $workspaceId: ID!) {
    upgradeToMember(userId: $userId, workspaceId: $workspaceId)
  }
`

export type UpgradeToMemberResults = UpgradeToMemberMutation['upgradeToMember']
export type UpgradeToMemberVariables = UpgradeToMemberMutationVariables

export const useUpgradeToMember = (): UseMutationResult<
  UpgradeToMemberResults,
  Error,
  UpgradeToMemberVariables
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['upgradeToMember'],
    mutationFn: async (variables: UpgradeToMemberVariables) => {
      const data = await graphql<
        UpgradeToMemberMutation,
        UpgradeToMemberVariables
      >({
        query,
        variables: variables
      })
      return data?.upgradeToMember
    },
    onSuccess: (_, { userId, workspaceId }) => {
      queryClient.setQueryData<GetWorkspaceByIdResults>(
        getWorkspaceByIdQueryKey(workspaceId),
        (prev) => {
          if (!prev) return prev

          return {
            ...prev,
            memberCollection: {
              total: prev.memberCollection.total + 1,
              items: [
                ...prev.memberCollection.items,
                {
                  id: `${workspaceId}:${userId}`,
                  role: Member_Role.Member
                }
              ]
            },
            guestCollection: {
              total: prev.guestCollection.items.some(
                (item) => item.user.id === userId
              )
                ? prev.guestCollection.total - 1
                : prev.guestCollection.total,
              items: prev.guestCollection.items.some(
                (item) => item.user.id === userId
              )
                ? prev.guestCollection.items.filter(
                    (item) => item.user.id !== userId
                  )
                : prev.guestCollection.items
            },
            invitedUserCollection: {
              total: prev.invitedUserCollection.items.some(
                (item) => item.userId === userId
              )
                ? prev.invitedUserCollection.total - 1
                : prev.invitedUserCollection.total,
              items: prev.invitedUserCollection.items.some(
                (item) => item.userId === userId
              )
                ? prev.invitedUserCollection.items.filter(
                    (item) => item.userId !== userId
                  )
                : prev.invitedUserCollection.items
            }
          } as GetWorkspaceByIdResults
        }
      )
      queryClient.invalidateQueries({
        queryKey: getWorkspaceByIdQueryKey(workspaceId),
        exact: true
      })
    }
  })
}
